import { createClient } from "@/app/utils/supabase/client";
import {
  FollowUpCampaign,
  FollowUpCampaignInsert,
  FollowUpCampaignUpdate,
  FollowUpCommunication,
  FollowUpCommunicationInsert,
  FollowUpCommunicationUpdate,
  FollowUpEntityType,
  TimelineHealth,
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";
import toast from "react-hot-toast";

export enum ThreadType {
  CAMPAIGN = "campaign",
  THREAD = "thread",
}

export interface Thread {
  id: string;
  shortId?: string;
  title: string;
  type: ThreadType;
  lastMessage?: string;
  lastMessageTime?: string | null;
  entityType?: FollowUpEntityType;
  entityId?: string | null;
  entityName?: string | null;
  healthStatus?: TimelineHealth;
}

export const followUpsApi = {
  // =====================================================
  // CAMPAIGNS
  // =====================================================

  async getCampaigns(companyId: string): Promise<FollowUpCampaign[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching follow-up campaigns:", error);
      throw error;
    }

    return data || [];
  },

  async getCampaignsByEntity(
    entityType: FollowUpEntityType,
    entityId: string
  ): Promise<FollowUpCampaign[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .select("*")
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching campaigns by entity:", error);
      throw error;
    }

    return data || [];
  },

  async getCampaign(campaignId: string): Promise<FollowUpCampaign> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .select("*")
      .eq("id", campaignId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching campaign:", error);
      throw error;
    }

    return data;
  },

  async createCampaign(campaign: FollowUpCampaignInsert): Promise<FollowUpCampaign> {
    const supabase = createClient();

    const uuid = crypto.randomUUID();
    const shortId = uuid.slice(-8).toUpperCase();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .insert({
        id: uuid,
        ...campaign,
        short_id: shortId.toLowerCase(),
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating campaign:", error);
      
      // Handle duplicate key constraint violation
      if (error.code === '23505' && error.message?.includes('follow_up_campaigns_entity_id_key')) {
        toast.error("A follow-up campaign for this entity already exists");
        throw error;
      }
      
      throw error;
    }

    return data;
  },

  async updateCampaign(
    campaignId: string,
    updates: FollowUpCampaignUpdate
  ): Promise<FollowUpCampaign> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", campaignId)
      .is("deleted_at", null)
      .select()
      .single();

    if (error) {
      console.error("Error updating campaign:", error);
      throw error;
    }

    return data;
  },

  async deleteCampaign(campaignId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", campaignId);

    if (error) {
      console.error("Error deleting campaign:", error);
      throw error;
    }
  },

  // =====================================================
  // COMMUNICATIONS
  // =====================================================

  async getCommunications(companyId: string): Promise<FollowUpCommunication[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .select("*")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("occurred_at", { ascending: false });

    if (error) {
      console.error("Error fetching communications:", error);
      throw error;
    }

    return data || [];
  },

  async getCommunicationsByCampaign(campaignId: string): Promise<FollowUpCommunication[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .select("*")
      .eq("follow_up_campaign_id", campaignId)
      .is("deleted_at", null)
      .order("occurred_at", { ascending: true });

    if (error) {
      console.error("Error fetching campaign communications:", error);
      throw error;
    }

    return data || [];
  },

  async getCommunicationsByThread(threadId: string): Promise<FollowUpCommunication[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .select("*")
      .eq("thread_id", threadId)
      .is("deleted_at", null)
      .order("occurred_at", { ascending: true });

    if (error) {
      console.error("Error fetching thread communications:", error);
      throw error;
    }

    return data || [];
  },

  async getCommunication(communicationId: string): Promise<FollowUpCommunication> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .select("*")
      .eq("id", communicationId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching communication:", error);
      throw error;
    }

    return data;
  },

  async createCommunication(communication: FollowUpCommunicationInsert): Promise<FollowUpCommunication> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .insert(communication)
      .select()
      .single();

    if (error) {
      console.error("Error creating communication:", error);
      throw error;
    }

    return data;
  },

  async updateCommunication(
    communicationId: string,
    updates: FollowUpCommunicationUpdate
  ): Promise<FollowUpCommunication> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", communicationId)
      .is("deleted_at", null)
      .select()
      .single();

    if (error) {
      console.error("Error updating communication:", error);
      throw error;
    }

    return data;
  },

  async deleteCommunication(communicationId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_COMMUNICATIONS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", communicationId);

    if (error) {
      console.error("Error deleting communication:", error);
      throw error;
    }
  },

  // =====================================================
  // UTILITY FUNCTIONS
  // =====================================================

  async getCampaignsByEntityIds(
    entityIds: string[],
    companyId: string
  ): Promise<Map<string, FollowUpCampaign[]>> {
    if (entityIds.length === 0) {
      return new Map();
    }

    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.FOLLOW_UP_CAMPAIGNS)
      .select("*")
      .eq("company_id", companyId)
      .in("entity_id", entityIds)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching campaigns by entity IDs:", error);
      throw error;
    }

    // Group campaigns by entity_id
    const campaignsMap = new Map<string, FollowUpCampaign[]>();
    (data || []).forEach((campaign) => {
      if (campaign.entity_id) {
        const existing = campaignsMap.get(campaign.entity_id) || [];
        campaignsMap.set(campaign.entity_id, [...existing, campaign]);
      }
    });

    return campaignsMap;
  },

  async getThreadsWithLastMessage(companyId: string) {
    // Get all campaigns
    const campaigns = await this.getCampaigns(companyId);

    // Get all communications
    const communications = await this.getCommunications(companyId);

    const supabase = createClient();

    // Collect all unique entity IDs by type
    const rfqIds = new Set<string>();
    const orderIds = new Set<string>();
    const batchIds = new Set<string>();

    [...campaigns, ...communications].forEach((item) => {
      if ('entity_type' in item && item.entity_id) {
        switch (item.entity_type) {
          case FollowUpEntityType.RFQ_SUPPLIER:
            rfqIds.add(item.entity_id);
            break;
          case FollowUpEntityType.ORDER:
            orderIds.add(item.entity_id);
            break;
          case FollowUpEntityType.BATCH:
            batchIds.add(item.entity_id);
            break;
        }
      }
    });

    // Fetch entities and create health status map
    const healthStatusMap = new Map<string, TimelineHealth>();
    const entityNameMap = new Map<string, string>();

    // Fetch RFQ suppliers
    if (rfqIds.size > 0) {
      const { data: rfqSuppliers } = await supabase
        .from(SUPABASE_TABLES.RFQ_SUPPLIERS)
        .select('id, timeline_health, suppliers(name), rfq_requests(name)')
        .in('id', Array.from(rfqIds));

      rfqSuppliers?.forEach(rfq => {
        if (rfq.timeline_health) {
          healthStatusMap.set(rfq.id, rfq.timeline_health as TimelineHealth);
        }
        const supplierName = (rfq.suppliers as unknown as {name: string})?.name;
        const rfqName = (rfq.rfq_requests as unknown as {name: string})?.name;
        const jointName = `${rfqName} - ${supplierName}`;
        entityNameMap.set(rfq.id, jointName);
      });
    }

    // Fetch orders
    if (orderIds.size > 0) {
      const { data: orders } = await supabase
        .from(SUPABASE_TABLES.ORDERS)
        .select('id, timeline_health, name, projects(name)')
        .in('id', Array.from(orderIds));

      orders?.forEach(order => {
        if (order.timeline_health) {
          healthStatusMap.set(order.id, order.timeline_health as TimelineHealth);
        }
        const projectName = (order.projects as unknown as {name: string})?.name;
        const jointName = `${order.name} - ${projectName}`;
        entityNameMap.set(order.id, jointName);
      });
    }

    // Fetch batches
    if (batchIds.size > 0) {
      const { data: batches } = await supabase
        .from(SUPABASE_TABLES.BATCHES)
        .select('id, timeline_health, name, projects(name)')
        .in('id', Array.from(batchIds));

      batches?.forEach(batch => {
        if (batch.timeline_health) {
          healthStatusMap.set(batch.id, batch.timeline_health as TimelineHealth);
        }
        const projectName = (batch.projects as unknown as {name: string})?.name;
        const jointName = `${batch.name} - ${projectName}`;
        entityNameMap.set(batch.id, jointName);
      });
    }

    // Process campaigns and standalone threads

    const threads: Thread[] = [];

    // Add campaigns as threads
    for (const campaign of campaigns) {
      const campaignComms = communications.filter(
        (comm) => comm.follow_up_campaign_id === campaign.id
      );
      const lastComm = campaignComms[0]; // Already sorted by occurred_at desc

      threads.push({
        id: campaign.id,
        shortId: campaign.short_id || undefined,
        title: campaign.title,
        type: ThreadType.CAMPAIGN,
        lastMessage: lastComm?.content_text?.substring(0, 50) || "",
        lastMessageTime: lastComm?.occurred_at || campaign.created_at,
        entityType: campaign.entity_type as FollowUpEntityType,
        entityId: campaign.entity_id,
        healthStatus: campaign.entity_id ? healthStatusMap.get(campaign.entity_id) : undefined,
        entityName: campaign.entity_id ? entityNameMap.get(campaign.entity_id) : undefined,
      });
    }

    // Find standalone threads
    const threadIds = new Set<string>();
    communications.forEach((comm) => {
      if (!comm.follow_up_campaign_id && comm.thread_id) {
        threadIds.add(comm.thread_id);
      }
    });

    for (const threadId of threadIds) {
      const threadComms = communications.filter((c) => c.thread_id === threadId);
      const lastComm = threadComms[0];
      const firstComm = threadComms[threadComms.length - 1];


      threads.push({
        id: threadId,
        title: lastComm?.subject || "Communication Thread",
        type: ThreadType.THREAD,
        lastMessage: lastComm?.content_text?.substring(0, 50) || "",
        lastMessageTime: lastComm?.occurred_at,
        entityType: firstComm?.entity_type as FollowUpEntityType,
        entityId: firstComm?.entity_id,
        healthStatus: firstComm?.entity_id ? healthStatusMap.get(firstComm.entity_id) : undefined,
        entityName: firstComm?.entity_id ? entityNameMap.get(firstComm.entity_id) : undefined,
      });
    }

    return threads;
  },
};